import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../config/api';
import LoadingIndicator from '../Components/LoadingIndicator';

const SubscriptionView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);

  const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    }).replace(',', ' ');
  };

  const formatStatus = (value) => {
    if (!value) return '-';
    const text = String(value).toLowerCase();
    return `${text.charAt(0).toUpperCase()}${text.slice(1)}`;
  };

  const getStatusTextClass = (label) => {
    const normalized = String(label || '').toLowerCase();
    if (normalized.includes('active') && !normalized.includes('inactive')) {
      return 'profile-value status-text status-active';
    }
    return 'profile-value status-text';
  };

  const createdByName = useMemo(() => {
    if (!data?.createdBy) return '-';
    if (typeof data.createdBy === 'string') return data.createdBy;
    return data.createdBy.name || data.createdBy.email || data.createdBy._id || '-';
  }, [data]);

  const updatedByName = useMemo(() => {
    if (!data?.updatedBy) return '-';
    if (typeof data.updatedBy === 'string') return data.updatedBy;
    return data.updatedBy.name || data.updatedBy.email || data.updatedBy._id || '-';
  }, [data]);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!API_URL) {
        toast.error('API URL not configured.');
        return;
      }
      if (!id) {
        toast.error('Subscription ID missing.');
        return;
      }

      setIsLoading(true);
      try {
        const response = await axios.post(API_URL, {
          action: 'getSubscription',
          subscriptionId: id
        }, {
          headers: { 'Content-Type': 'application/json' }
        });

        setData(response.data || null);
      } catch (error) {
        const message = error.response?.data?.message || error.message || 'Failed to load subscription';
        toast.error(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchSubscription();
  }, [id]);

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="row align-items-center">
          <div className="col-sm-6">
            <h1>Subscription Details</h1>
            <p>View subscription details.</p>
          </div>
          <div className="col-sm-6 text-right subscription-header-actions">
            <button type="button" className="btn btn-secondary" onClick={() => navigate(-1)}>
              Back
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="employee-loading">
          <LoadingIndicator size={120} />
        </div>
      ) : !data ? (
        <div className="card employee-content-card">
          <div className="card-body">
            <div className="employee-empty">No subscription data found.</div>
          </div>
        </div>
      ) : (
        <>
          {/* Plan Information */}
          <div className="detail-section">
            <div className="detail-section-header">
              <i className="fas fa-crown"></i>
              <h3>Plan Information</h3>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon">
                  <i className="fas fa-box"></i>
                </div>
                <div className="detail-content">
                  <label>Plan</label>
                  <span className="detail-value highlight">{data.planName || '-'}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon status">
                  <i className="fas fa-check-circle"></i>
                </div>
                <div className="detail-content">
                  <label>Status</label>
                  <span className={`status-badge ${data.status?.toLowerCase()}`}>
                    {formatStatus(data.status)}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Subscription Details */}
          {(data.startDate || data.endDate || data.duration) && (
            <div className="detail-section">
              <div className="detail-section-header financial">
                <i className="fas fa-calendar-alt"></i>
                <h3>Subscription Period</h3>
              </div>
              <div className="detail-grid">
                {data.startDate && (
                  <div className="detail-item">
                    <div className="detail-icon">
                      <i className="fas fa-calendar-check"></i>
                    </div>
                    <div className="detail-content">
                      <label>Start Date</label>
                      <span className="detail-value">{formatDateTime(data.startDate)}</span>
                    </div>
                  </div>
                )}
                {data.endDate && (
                  <div className="detail-item">
                    <div className="detail-icon">
                      <i className="fas fa-calendar-times"></i>
                    </div>
                    <div className="detail-content">
                      <label>End Date</label>
                      <span className="detail-value">{formatDateTime(data.endDate)}</span>
                    </div>
                  </div>
                )}
                {data.duration && (
                  <div className="detail-item">
                    <div className="detail-icon">
                      <i className="fas fa-hourglass-half"></i>
                    </div>
                    <div className="detail-content">
                      <label>Duration</label>
                      <span className="detail-value">{data.duration}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Additional Information */}
          {(data.description || data.features) && (
            <div className="detail-section">
              <div className="detail-section-header">
                <i className="fas fa-info-circle"></i>
                <h3>Additional Details</h3>
              </div>
              <div className="detail-grid">
                {data.description && (
                  <div className="detail-item full-width">
                    <div className="detail-icon">
                      <i className="fas fa-align-left"></i>
                    </div>
                    <div className="detail-content">
                      <label>Description</label>
                      <span className="detail-value summary">{data.description}</span>
                    </div>
                  </div>
                )}
                {data.features && (
                  <div className="detail-item full-width">
                    <div className="detail-icon">
                      <i className="fas fa-list-ul"></i>
                    </div>
                    <div className="detail-content">
                      <label>Features</label>
                      <span className="detail-value summary">{data.features}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Audit Information */}
          <div className="detail-section audit">
            <div className="detail-section-header audit">
              <i className="fas fa-history"></i>
              <h3>Audit Trail</h3>
            </div>
            <div className="detail-grid">
              <div className="detail-item">
                <div className="detail-icon audit">
                  <i className="fas fa-user-plus"></i>
                </div>
                <div className="detail-content">
                  <label>Created By</label>
                  <span className="detail-value">{createdByName}</span>
                  <span className="detail-timestamp">{formatDateTime(data.createdAt)}</span>
                </div>
              </div>
              <div className="detail-item">
                <div className="detail-icon audit">
                  <i className="fas fa-user-edit"></i>
                </div>
                <div className="detail-content">
                  <label>Updated By</label>
                  <span className="detail-value">{updatedByName}</span>
                  <span className="detail-timestamp">{formatDateTime(data.updatedAt)}</span>
                </div>
              </div>
            </div>
          </div>         
        </>
      )}
    </div>
  );
};

export default SubscriptionView;